/**
 * EVERY FACT THAT IS DERIVED RATHER THAN STORED, DERIVED IN ONE PLACE.
 *
 * This file exists because of a specific, repeated defect: the same fact worked out
 * independently in two files, and the two answers drifting apart. It has happened twice,
 * both times silently, both times producing output that looked entirely reasonable.
 *
 * The rule for anything added here: if a question has one right answer given the data,
 * this is where that answer is computed, and every caller asks rather than works it out.
 */

import type { Corpus, Verse } from './types.js';

/**
 * The tail of a reference: its chapter and its verse.
 *
 * ONE pattern, because everything that takes a reference apart or puts one together has to
 * agree about its shape. It covers numbered chapters ("Genesis 1:1") and Talmudic folios
 * ("Berakhot 2a:1") alike — and the folio branch is exactly what a second copy of this,
 * written out by hand in the browser, had quietly lost.
 *
 * No `g` flag: a shared regex carrying `lastIndex` between callers is its own bug.
 */
const TAIL = /\s+(\d+[ab]?):(\d+)\s*$/;

/**
 * Which book a reference belongs to.
 *
 * The reference is the authority, not the stored book field, because the reference is
 * what a reader sees and cites. The two disagree for the Quran — the field reads
 * "Surah 2 Al-Baqarah", the reference "Quran 2:255" — and keying on the field left every
 * Quranic passage unable to find its own translation.
 */
export function bookOfRef(ref: string): string {
  return String(ref == null ? '' : ref).replace(TAIL, '');
}

/** Which chapter it names — "1" for Genesis 1:1, "2a" for the first side of Berakhot 2. */
export function chapterOfRef(ref: string): string {
  const m = TAIL.exec(String(ref == null ? '' : ref));
  return m ? m[1] : '';
}

/**
 * The key under which a book's chosen editions are recorded.
 *
 * Deliberately the BOOK, not the passage unit below: one translator rendered the whole
 * Quran, and its editions are filed under `QURAN|Quran`. Who translated a passage and how
 * far a passage extends are two questions, and they have different answers.
 */
export function bookKey(corpusId: string, ref: string): string {
  return `${corpusId}|${bookOfRef(ref)}`;
}

/**
 * The unit a passage may be read within — what "the verses around this one" stops at.
 *
 * See Corpus.passageUnit for why a text states this rather than having it inferred from
 * the shape of its citations. In short: three of the four texts name their book in every
 * reference and one does not, so stripping the numbers gave a single unit for all 6,236
 * Quranic verses — which is not a boundary, it is the absence of one.
 *
 * A corpus that says nothing falls back to the book, which was always the right answer
 * for the texts whose references name their books.
 */
export function passageUnitOf(corpus: Pick<Corpus, 'passageUnit'> | undefined, ref: string): string {
  const book = bookOfRef(ref);
  if (!corpus || corpus.passageUnit !== 'chapter') return book;
  const ch = chapterOfRef(ref);
  return ch ? `${book} ${ch}` : book;
}

/**
 * Whether a gazetteer spelling listed under `scope` may match a verse in this text.
 *
 * Written out as `corpus === 'HB' || corpus === 'NT'` this silently excluded every text
 * added later: Moses could not be linked in the Mishnah or the Talmud however often he is
 * named there, so every hand-checked identity stopped at the Bible.
 */
export function scopeApplies(corpus: Corpus | undefined, scopes: Iterable<string>): boolean {
  if (!corpus) return false;
  const here = corpus.spellingScopes || [];
  for (const s of scopes) if (here.includes(s)) return true;
  return false;
}

/** The corpus a packed corpus-code belongs to. */
export function corpusOfCode(corpora: Corpus[], code: string): Corpus | undefined {
  return corpora.find(c => c.code === code);
}

/** Does this library carry anything that may not be used commercially? */
export function hasNonCommercial(editions: { nonCommercial?: boolean }[]): boolean {
  return (editions || []).some(e => !!e.nonCommercial);
}

/**
 * Whether two references sit in the same passage unit.
 *
 * "The verses around this one" is id ± 1, which is only meaningful while the unit holds.
 * Running off the end of Malachi into Matthew is not context, it is a different book; and
 * running off the end of Al-Baqarah into Al-Imran is not context either, which the version
 * of this that compared books alone could not say.
 */
export function samePassage(corpus: Pick<Corpus, 'passageUnit'> | undefined, refA: string, refB: string): boolean {
  return passageUnitOf(corpus, refA) === passageUnitOf(corpus, refB);
}

/** A verse's canonical reference, built from its parts. Kept beside the parser of the
 *  same form so the two can never disagree about the shape of a reference. */
export function refOf(book: string, chapter: string | number, verse: number): string {
  return `${book} ${chapter}:${verse}`;
}

/**
 * Verse ids are POSITIONS in the reading order, so a book must occupy one unbroken run.
 * Returns the books that do not — an empty array means the order is sound.
 */
export function splitBooks(verses: Pick<Verse, 'corpus' | 'ref'>[]): string[] {
  const seen = new Set<string>();
  const broken: string[] = [];
  let prev: string | null = null;
  for (const v of verses) {
    const key = bookKey(v.corpus, v.ref);
    if (key === prev) continue;
    if (seen.has(key)) broken.push(key);
    seen.add(key);
    prev = key;
  }
  return broken;
}

/**
 * THE WORDS A PASSAGE IS SEARCHABLE BY — one implementation, because there were two.
 *
 * build-web-data built the index with `/[a-z]{3,}/` and qa-claims re-derived it with the
 * same regex written out again, which is the arrangement this package exists to end: the
 * moment one was fixed the other went on disagreeing, and the verifier reported the
 * shipped index as wrong when it had just been made right.
 *
 * ASCII-only was itself the bug. `[a-z]` does not skip a letter outside it — it ENDS the
 * word there, so the King James's "Judæa" and "Ænon" were indexed as "jud" and "non":
 * fragments that are not words, while the words themselves could not be found at all.
 *
 * `\p{Ll}` after lowercasing, plus `\p{M}` so a decomposed accent stays attached to its
 * base letter rather than splitting one word into two.
 *
 * AND A HYPHENATED NAME IS ALSO ONE WORD. A reader types "Bethlehem"; this King James
 * writes "Beth–lehem", and the index held "beth" and "lehem" and nothing a reader would
 * ever type. Measured on the shipped index: "bethlehem" was absent from the Hebrew Bible
 * entirely, as were "beersheba" and "bathsheba" — so the map could show thirty verses for
 * a name that search swore was nowhere in the text. Both halves are still indexed, because
 * "Beth" and "lehem" are what someone reading the page sees.
 *
 * WHY `\p{Lo}` IS NOT HERE, AND WHY IT IS NOT AN OVERSIGHT — measured 2026-08-20.
 *
 * `\p{Ll}` is LOWERCASE letters, so Greek indexes and Hebrew, Arabic and Syriac do not: those
 * scripts are caseless `\p{Lo}`. It looks like a one-character fix, and the pages now show the
 * Hebrew and the Greek, so the obvious next step is to let a reader search them. It does not work,
 * for a reason that is about the languages rather than about this regex.
 *
 * `build-web-data` indexes `englishOf(v)` only, so nothing is silently dropped today. Were the
 * originals added, a 20,000-passage sample yields 96,169 distinct surface forms against a whole
 * shipped index of 4.25 MB — and the size is the lesser problem. A reader searching for עלמה, the
 * word Isaiah 7:14 turns on, would find nothing: the text holds הָעַלְמָה, with the article
 * attached, and matching by consonants instead returns וְנֶעֶלְמָה, נִתְעַלְּמָה and
 * שֶׁנִּתְעַלְּמָה — different words that share a root.
 *
 * Making it work needs a morphological model: a claim that ה is an article here and part of the
 * word there, per language. That is the same kind of claim this project refuses when it declines
 * to normalise "Bukhari" and "Al-Bukhāri" — an editorial decision wearing data-cleaning as a
 * costume. So searching the originals is a feature with a linguistic prerequisite, not a flag.
 */
export function searchTokens(text: unknown): string[] {
  const lower = normDash(String(text)).toLowerCase();
  const out: string[] = [];
  /* Any length here, then filtered below: "Beth–el" must contribute "bethel", and a
     three-letter floor applied to the PARTS would have thrown "el" away before the whole
     could be assembled. */
  for (const run of lower.match(/[\p{Ll}\p{M}]+(?:-[\p{Ll}\p{M}]+)*/gu) || []) {
    const parts = run.split('-');
    for (const p of parts) if (p.length >= 3) out.push(p);
    if (parts.length > 1) {
      const joined = parts.join('');
      if (joined.length >= 3) out.push(joined);
    }
  }
  /* Folded, so the index holds one spelling and a reader typing ASCII reaches it. The
     query is folded by the same function in core/search.ts — see foldLatin below. */
  return out.map(foldLatin);
}

/**
 * ONE SPELLING FOR MATCHING, so a reader typing ASCII finds a word that is not.
 *
 * The King James spells Judaea "Judæa" and Aenon "Ænon". Indexing those as themselves was
 * already an improvement on cutting them into "jud" and "non", but it left the reader no
 * better off: typing "Judaea" still answered "Nothing found" about a place named in the
 * text in front of them, in 123 verses of the American Standard Version alone.
 *
 * WHAT THE TABLE IS AND IS NOT. NFD decomposes an accented letter into a base and a
 * combining mark, so stripping marks handles ï, ü, ḥ and every other letter built that
 * way — that is the general rule and it is not enumerated. What NFD cannot decompose is a
 * letter that is not a base plus an accent: æ is not "a with a mark", it is its own
 * letter. Those need saying, and only those.
 *
 * Measured against the published library rather than guessed: it contains æ, Æ, ï and ü,
 * and nothing else outside ASCII in the Latin range. œ, ø, ß, þ and ð are here because
 * they are the rest of the class NFD cannot reach, not because a text uses them yet.
 */
const UNDECOMPOSABLE: [RegExp, string][] = [
  [/æ/g, 'ae'], [/œ/g, 'oe'], [/ø/g, 'o'], [/ß/g, 'ss'], [/þ/g, 'th'], [/ð/g, 'd'],
];

/**
 * ʿAYN AND HAMZA ARE INSIDE THE WORD, AND NFD CANNOT SEE THEM.
 *
 * `\p{M}` strips combining marks, which is why Abū folds to abu and Bilāl to bilal. The Arabic
 * transliteration marks are not marks: U+02BF and U+02BE are modifier LETTERS, and the
 * typographic forms are punctuation. They survive the fold, sit in the middle of a name, and
 * stop it matching anything — ʿĀʾishah folds to "ʿaʾishah", which is not "aisha" and never
 * will be. It is the same shape as the RIGHT-TO-LEFT MARK surviving a mark-strip in
 * `scripts.ts`: a character nobody sees, in a class nobody stripped.
 *
 * It surfaced when the hadith gained an English translation. HadeethEnc writes scholarly
 * transliteration — Abu Saʿīd, Ibn Masʿūd, al-Ashʿari — and U+2018 alone appears 3,443 times
 * in 2,328 passages.
 *
 * U+2018 IS SAFE TO DELETE AND U+2019 IS NOT, which is the whole reason this is a list rather
 * than a character class. U+2018 is an OPENING quote: no English word contains one, so every
 * occurrence here is an ʿayn. U+2019 is the apostrophe English actually uses — "Allah’s" —
 * and appears 1,046 times doing both jobs. Deleting it would silently join possessives, so it
 * is left to the tokeniser.
 */
const TRANSLITERATION_MARKS = /[ʿʾ‘]/g;

export function foldLatin(word: string): string {
  let out = word.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase();
  out = out.replace(TRANSLITERATION_MARKS, '');
  for (const [from, to] of UNDECOMPOSABLE) out = out.replace(from, to);
  return out;
}

/**
 * THE HYPHEN IN A HYPHENATED NAME, WHICHEVER CHARACTER THE EDITION USED FOR IT.
 *
 * This King James writes its compound names with U+2013 EN DASH: "Beth–lehem",
 * "Beer–sheba", "Bath–sheba". Every tokeniser in this project splits words on an ASCII
 * hyphen and nothing else, so those names arrived as two fragments and no gazetteer
 * spelling could reach them. Bethlehem — the town of David, and of the nativity — held
 * ZERO Hebrew Bible verses on the map while the text names it thirty times.
 *
 * The extractor already knew: it has carried this exact substitution, with "Beth–el ->
 * Beth-el" written in the comment beside it, since dashes first caused trouble there. The
 * matcher never got it. That is the whole defect — one rule about what a hyphen is, known
 * in one file and absent from the other, so the map and the entity list read the same
 * sentence differently.
 *
 * Measured before moving it here: of the dashes in the published text, the em dash NEVER
 * appears between two letters, and all 815 en dashes that do are inside proper names. So
 * this cannot fuse two words that punctuation had merely separated. Each replacement is
 * one character for one character, so any offset taken against the original still holds.
 */
const INTRA_WORD_DASH = /[‐‑–—]/g;

export function normDash(text: string): string {
  return String(text).replace(INTRA_WORD_DASH, '-');
}

/**
 * THE ENGLISH RENDERINGS A PASSAGE HAS — up to three, and all of them count.
 *
 * The Quran travels with the Rowwad Translation Center and Pickthall, the New Testament
 * with the King James and the American Standard Version, and the Hebrew Bible with the
 * King James, the Berean and the JPS 1917, for the same reason in every case: a
 * translation is an interpretation, and one of them alone can drive a result unremarked.
 *
 * Written out separately in build-web-data as `englishOf` and in qa-claims as
 * `renderings`, and NOT written at all in build-graph, which read `v.text` and stopped —
 * so the search index knew about both and the map knew about one. "Most Gracious" occurs
 * 54 times in Yusuf Ali and never in Pickthall: findable, and on the map nowhere.
 *
 * THE THIRD WAS MISSED THE SAME WAY, ON THE DAY IT WAS ADDED — 2026-08-15. The Hebrew
 * Bible gained the JPS 1917 and this function still returned two, so a Jewish translation
 * was rendered under every Old Testament verse while being invisible to both the search
 * index and the map that read through here. Its whole reason for being carried is that it
 * words things differently — "the young woman" where the others read "a virgin" — and
 * every one of those differences was unfindable. One function, both consumers, one line:
 * which is the argument for it having been made shared in the first place.
 */
export function englishOf(
  v: Pick<Verse, 'text'> & { altTranslation?: string; altTranslation2?: string },
): string[] {
  return [v.text, v.altTranslation, v.altTranslation2].filter((t): t is string => !!t);
}
